import { Divider } from '@components/core-ui/Divider';
import { TextButton } from '@components/core-ui/form-elements/buttons/Button/variations/TextButton';
import { Header } from '@components/Header';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OrganizationsService } from 'app/API';
import { organizationsKeys } from 'app/queries/organizations/organizationsKeys';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { createFetchUserDetailsQueryFn } from './api/fetchUserDetails';
import { JobHistory } from './JobHistory';
import { Organizations } from './Organizations';
import { Content } from './styles/Content';
import { Teams } from './Teams';
import { UserDetails } from './UserDetails';
import { Workspaces } from './Workspaces';

type TCanManageLayoutsUpdate = {
  organizationId: string;
  userId: string;
  membershipId: string;
  value: boolean;
};

export const UserDetailsPage = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { organization, me } = useAuthContext();
  const { userId = '' } = useParams<{ userId: string }>();
  const organizationId = organization?.id ?? '';
  const isVsAdmin = me?.roles?.includes('ROLE_VS_ADMIN');

  const userDetailsQuery = useQuery({
    queryKey: organizationsKeys.memberDetail(organizationId, userId),
    queryFn: createFetchUserDetailsQueryFn(call),
    enabled: organizationId !== '' && userId !== '',
  });
  const user = userDetailsQuery.data?.result;

  const invalidateUserDetails = () =>
    queryClient.invalidateQueries({ queryKey: organizationsKeys.memberDetail(organizationId, userId) });

  const canManageLayoutsMutation = useMutation({
    mutationFn: ({ organizationId, membershipId, value }: TCanManageLayoutsUpdate) =>
      call(() =>
        OrganizationsService.setMemberCanManageLayoutFlag({
          oid: organizationId,
          memberId: membershipId,
          requestBody: { value },
        }),
      ),
    onError: () => toast.error('Failed to update the cloud layouts permission'),
    onSettled: (_data, _error, variables) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: organizationsKeys.memberDetail(variables.organizationId, variables.userId),
        }),
        queryClient.invalidateQueries({ queryKey: organizationsKeys.membersByOrg(variables.organizationId) }),
      ]),
  });

  const isLoading = userDetailsQuery.isFetching || canManageLayoutsMutation.isPending;

  return (
    <>
      <Helmet>
        <title>User Details - AutoScript</title>
      </Helmet>
      <Header title="User Details" />

      <LinearProgressLoader active={isLoading} />
      <Content>
        {userDetailsQuery.isError ? (
          <div className="mb-4 flex items-center gap-3 text-xs text-[var(--color-text-muted)]" role="alert">
            <span>Failed to load the user details.</span>
            <TextButton type="button" onClick={() => void userDetailsQuery.refetch()}>
              Retry
            </TextButton>
          </div>
        ) : null}
        {user ? (
          <>
            <UserDetails
              user={user}
              reload={() => void invalidateUserDetails()}
              isUpdatingCanManageLayouts={canManageLayoutsMutation.isPending}
              onCanManageLayoutsChange={(membershipId, value) =>
                canManageLayoutsMutation.mutate({ organizationId, userId, membershipId, value })
              }
            />
            <Divider />
            {isVsAdmin ? (
              <>
                <Organizations user={user} />
                <Divider />
              </>
            ) : null}
            <Workspaces user={user} />
            <Divider />
            <Teams user={user} />
            <Divider />
            <JobHistory user={user} />
          </>
        ) : null}
      </Content>
    </>
  );
};
